import { useState, useEffect } from 'react';
import { ContainerClient } from '@azure/storage-blob';

interface Image {
  name: string;
  url: string;
}

interface UseAzureBlobImageResult {
  image: Image | null;
  loading: boolean;
  error: string | null;
}

// Cache global para evitar llamadas duplicadas
const imageCache = new Map<string, Image>();

const useAzureBlobImage = (imageName: string): UseAzureBlobImageResult => {
  const [image, setImage] = useState<Image | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Verificar cache primero, solo para imágenes reales
    if (imageCache.has(imageName)) {
      const cachedImage = imageCache.get(imageName);

      if (cachedImage && cachedImage.url !== '/placeholder.jpg') {
        setImage(cachedImage);
        setLoading(false);
        return;
      }
      console.log(
        `Hook: Placeholder ${imageName} en cache, intentando cargar imagen real.`,
      );
    }

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(`Hook: Cargando imagen ${imageName} desde Azure...`);

        const storageAccount = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT;
        const containerName = process.env.REACT_APP_AZURE_BLOB_CONTAINER;
        const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

        // Verifica que las variables de entorno estén definidas
        if (!storageAccount || !containerName || !sasToken) {
          throw new Error(
            'Faltan variables de entorno para Azure Blob Storage',
          );
        }

        const containerSasUrl = `https://${storageAccount}.blob.core.windows.net/${containerName}?${sasToken}`;
        // Crea un cliente de contenedor con la URL SAS
        const containerClient = new ContainerClient(containerSasUrl);
        let blobClient = containerClient.getBlobClient(`${imageName}.jpg`);
        // Verifica que el blob existe
        let properties = null;

        try {
          properties = await blobClient.getProperties();
        } catch {
          // Si falla, intentamos con .png
          try {
            blobClient = containerClient.getBlobClient(`${imageName}.png`);
            properties = await blobClient.getProperties();
          } catch {
            properties = null; 
          }
        }

        let finalUrl = '/placeholder.jpg';
        
        if (properties && properties.lastModified) {
          // Convertimos la fecha de modificación en un número (timestamp)
          const version = properties.lastModified.getTime();
          // Añadimos &t=[timestamp] al final de la URL que ya trae el SAS token
          finalUrl = `${blobClient.url}&t=${version}`;
        }

        const imageResult: Image = {
          name: imageName,
          url: finalUrl,
        };

        // Guardar en cache
        imageCache.set(imageName, imageResult);
        setImage(imageResult);
        setLoading(false);

        console.log(`Hook: Imagen ${imageName} cargada y guardada en cache`);
      } catch (err) {
        console.error(`Hook: Error cargando imagen ${imageName}:`, err);

        const fallbackImage: Image = {
          name: imageName,
          url: '/placeholder.jpg',
        };

        imageCache.set(imageName, fallbackImage);
        setImage(fallbackImage);

        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Error desconocido');
        }
        setLoading(false);
      }
    };

    fetchImage();
  }, [imageName]);

  return { image, loading, error };
};

export default useAzureBlobImage;
